import type { FragmentType } from 'src/gql';
import type { Household } from 'src/types/household';
import type { SideBarItemProps } from 'src/utils/advisor-dash-nav/side-bar';
import { isNil } from 'es-toolkit';
import { reconcileSectionOrder } from 'src/components/shared/reorder';
import { graphql, unmaskFragment } from 'src/gql';
import { isClient } from 'src/utils/advisor-dash/household';

import type { ID } from '@savvy/utils/id';
import { USER_ROLE_NON_ADVISORY_USER } from '@savvy/constants/user-roles';

import { OVERVIEW_TAB_TITLE } from './get-active-tab';

export const GetSideBarItemsFragment = graphql(/* GraphQL */ `
  fragment GetSideBarItems on Client {
    user {
      role
    }
    household {
      householdStage
      taxEnrollmentBannerStatus
      enableProspectTax
    }
  }
`);

export function getSideBarItems({
  clientFrag,
  personalPortfolioId,
  secondaryPortfolios,
  household,
  flags,
}: {
  clientFrag: FragmentType<typeof GetSideBarItemsFragment> | undefined;
  personalPortfolioId: ID | undefined;
  secondaryPortfolios:
    | {
        id: ID;
        name: string;
        displayName: string | null;
      }[]
    | undefined;
  household?: Household;
  flags?: { docusignStatusTracker?: boolean; financialPlanner?: boolean; serviceCalendarV3?: boolean };
}): SideBarItemProps[] {
  if (!household) {
    return [];
  }
  const client = unmaskFragment(GetSideBarItemsFragment, clientFrag);
  const salesforceAccountId = household.salesforceAccountId;

  let secondaryPortfolioItems: { title: string; path: string }[] = [];

  if (secondaryPortfolios) {
    secondaryPortfolioItems = secondaryPortfolios.map((portfolio) => ({
      title: portfolio.displayName || portfolio.name,
      path: `/advisor/dashboard/client/${salesforceAccountId}/portfolio/${portfolio.id}`,
    }));
  }

  const strategyItem: { title: string; path: string; disabled: boolean }[] = [];
  strategyItem.push({
    title: 'Strategy',
    path: `/advisor/dashboard/client/${salesforceAccountId}/portfolio/strategy`,
    disabled: !!household.adviceOnly || isNil(household.orionHouseholdId),
  });

  const sideBarItems: SideBarItemProps[] = [
    {
      title: OVERVIEW_TAB_TITLE,
      path: `/advisor/dashboard/client/${salesforceAccountId}`,
    },
    {
      title: 'Details',
      path: `/advisor/dashboard/client/${salesforceAccountId}/details`,
    },
    {
      title: 'Portfolio',
      path:
        !household.adviceOnly && household.orionHouseholdId
          ? `/advisor/dashboard/client/${salesforceAccountId}/portfolio/${personalPortfolioId}`
          : `/advisor/dashboard/client/${salesforceAccountId}/net-worth`,
      disabled: client?.user.role === USER_ROLE_NON_ADVISORY_USER,
      items: [
        {
          title: 'Net worth',
          path: `/advisor/dashboard/client/${salesforceAccountId}/net-worth`,
          disabled: client?.user.role === USER_ROLE_NON_ADVISORY_USER,
        },
        {
          title: secondaryPortfolios && secondaryPortfolios.length > 0 ? 'Personal investments' : 'Managed investments',
          path: `/advisor/dashboard/client/${salesforceAccountId}/portfolio/${personalPortfolioId}`,
          disabled: !!household.adviceOnly || isNil(household.orionHouseholdId),
        },
        ...secondaryPortfolioItems,
        ...strategyItem,
      ],
    },
    {
      title: 'Planning',
      path: `/advisor/dashboard/client/${salesforceAccountId}/planning`,
    },
    ...(flags?.financialPlanner
      ? [
          {
            title: 'Retirement planning',
            path: `/advisor/dashboard/client/${salesforceAccountId}/retirement-planning`,
          },
        ]
      : []),
    {
      title: 'Risk Questionnaire',
      path: `/advisor/dashboard/client/${salesforceAccountId}/risk-questionnaire`,
    },
    {
      title: 'Library',
      path: `/advisor/dashboard/client/${salesforceAccountId}/files`,
      items: [
        {
          title: 'Files',
          path: `/advisor/dashboard/client/${salesforceAccountId}/files`,
        },
        {
          title: 'Shared materials',
          path: `/advisor/dashboard/client/${salesforceAccountId}/shared-materials`,
        },
      ],
    },
    {
      title: 'Service calendar',
      path: `/advisor/dashboard/client/${salesforceAccountId}/service-calendar`,
      disabled: !isClient(client?.household.householdStage),
    },
    ...(flags?.serviceCalendarV3
      ? [
          {
            title: 'Service calendar v3',
            path: `/advisor/dashboard/client/${salesforceAccountId}/service-calendar-v3`,
            disabled: !isClient(client?.household.householdStage),
          },
        ]
      : []),
  ];

  if (isClient(client?.household.householdStage)) {
    sideBarItems.push({
      title: 'Opportunities',
      path: `/advisor/dashboard/client/${salesforceAccountId}/opportunities`,
    });
  }

  if (client?.household.enableProspectTax || isClient(client?.household.householdStage)) {
    sideBarItems.push({
      title: 'Tax',
      path: `/advisor/dashboard/client/${salesforceAccountId}/tax`,
      showBadge:
        !isNil(client?.household.taxEnrollmentBannerStatus) && client.household.taxEnrollmentBannerStatus !== 'none',
    });
  }

  if (flags?.docusignStatusTracker) {
    sideBarItems.push({
      title: 'Paperwork',
      path: `/advisor/dashboard/client/${salesforceAccountId}/paperwork`,
    });
  }

  return sideBarItems;
}

// Keyed by title, not path (embeds the household id) or index (several items are conditional).
// Runs before the disabled-last sort, so a saved order only holds within a bucket, not across both.
export function applyTabOrder(items: readonly SideBarItemProps[], tabOrder: readonly string[]): SideBarItemProps[] {
  const byTitle = new Map(items.map((item) => [item.title, item]));
  return reconcileSectionOrder(
    tabOrder,
    items.map((item) => item.title),
  ).flatMap((title) => byTitle.get(title) ?? []);
}
